import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Divider,
  Tooltip,
  Slider,
} from '@mui/material';
import {
  Search,
  Add,
  Download,
  FilterList,
  Edit,
  Delete,
  TrendingUp,
  TrendingDown,
  Assessment,
  Science,
  Flag,
  BarChart,
  PieChart,
  Timeline,
  Group,
  Person,
  Business,
  Cancel,
  CheckCircle,
  Schedule,
  Cached,
} from '@mui/icons-material';

interface FeatureFlag {
  id: string;
  name: string;
  status: 'Active' | 'Inactive';
  rollout: number;
  targetGroup: 'Drivers' | 'Renters' | 'Hosts' | 'All Users' | 'Region: Lagos';
  created: string;
  lastModified: string;
  description?: string;
}

interface ExperimentMetric {
  category: 'Conversion' | 'Cancellation' | 'UX Friction';
  metrics: {
    name: string;
    current: number;
    previous?: number;
    change?: number;
    isPositive?: boolean;
  }[];
  timeRange: 'Last 7 Days' | 'Last 30 Days' | 'Custom';
}

interface ToggleStrategy {
  id: string;
  name: 'Role-Based Targeting' | 'Percentage Rollout' | 'Region-Based Targeting' | 'Custom Rules';
  description: string;
  isActive: boolean;
  settings?: any;
}

export const ExperimentationToolsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus] = useState('all');
  const [newExperimentOpen, setNewExperimentOpen] = useState(false);
  const [newToggleOpen, setNewToggleOpen] = useState(false);
  const [newToggleForm, setNewToggleForm] = useState({
    name: '',
    description: '',
    targetGroup: 'All Users',
    rolloutPercentage: 0
  });
  const [roleTargetingSettings, setRoleTargetingSettings] = useState({
    'Hosts / Owners': {
      enabled: true,
      roles: {
        'Owners': true,
        'Brokers': true,
        'Liaisons': true,
        'Fleet Managers': true
      }
    },
    'Renters / Drivers': {
      enabled: true,
      roles: {
        'All Renters': true,
        'First-time Renters': true,
        'Verified Drivers': true,
        'Business Accounts': true
      }
    },
    'Admin / Support': {
      enabled: true,
      roles: {
        'Super Admins': true,
        'Support Agents': true,
        'Finance Team': true,
        'Verification Team': true
      }
    }
  });

  // Mock feature flags data
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([
    {
      id: '1',
      name: 'New Driver Dashboard',
      status: 'Active',
      rollout: 25,
      targetGroup: 'Drivers',
      created: 'May 12, 2025',
      lastModified: 'Jun 28, 2025'
    },
    {
      id: '2',
      name: 'Instant Booking Flow',
      status: 'Active',
      rollout: 50,
      targetGroup: 'Renters',
      created: 'Apr 18, 2025',
      lastModified: 'Jun 24, 2025'
    },
    {
      id: '3',
      name: 'Enhanced Verification',
      status: 'Inactive',
      rollout: 0,
      targetGroup: 'Hosts',
      created: 'Jun 05, 2025',
      lastModified: 'Jun 05, 2025'
    },
    {
      id: '4',
      name: 'Dynamic Pricing Model',
      status: 'Active',
      rollout: 15,
      targetGroup: 'Region: Lagos',
      created: 'May 30, 2025',
      lastModified: 'Jun 15, 2025'
    },
    {
      id: '5',
      name: 'Trust Code UI Redesign',
      status: 'Active',
      rollout: 75,
      targetGroup: 'All Users',
      created: 'Jun 01, 2025',
      lastModified: 'Jun 20, 2025'
    }
  ]);

  // Mock experiment metrics
  const experimentMetrics: ExperimentMetric[] = [
    {
      category: 'Conversion',
      timeRange: 'Last 7 Days',
      metrics: [
        { name: 'Search to Booking', current: 23.8, previous: 21.4, change: 2.4, isPositive: true },
        { name: 'Booking Completion', current: 68.5, previous: 65.2, change: 3.3, isPositive: true },
        { name: 'Driver Request Rate', current: 42.1, previous: 45.8, change: -3.7, isPositive: false },
        { name: 'Repeat Booking Rate', current: 34.2, previous: 33.1, change: 1.1, isPositive: true }
      ]
    },
    {
      category: 'Cancellation',
      timeRange: 'Last 30 Days',
      metrics: [
        { name: 'Host Cancellation', current: 7.2, previous: 8.6, change: -1.4, isPositive: true },
        { name: 'Guest Cancellation', current: 5.4, previous: 6.1, change: -0.7, isPositive: true },
        { name: 'Substitution Rate', current: 12.3, previous: 14.8, change: -2.5, isPositive: true },
        { name: 'Driver No-Show', current: 3.1, previous: 3.8, change: -0.7, isPositive: true }
      ]
    },
    {
      category: 'UX Friction',
      timeRange: 'Last 30 Days',
      metrics: [
        { name: 'Avg. Booking Time', current: 342, change: 0.8, isPositive: false }, // in seconds
        { name: 'Form Abandonment', current: 18.3, previous: 21.6, change: -3.3, isPositive: true },
        { name: 'Support Contacts', current: 8.9, previous: 7.6, change: 1.3, isPositive: false },
        { name: 'App Crashes', current: 0.8, previous: 1.4, change: -0.6, isPositive: true }
      ]
    }
  ];

  // Mock toggle strategies
  const toggleStrategies: ToggleStrategy[] = [
    {
      id: '1',
      name: 'Role-Based Targeting',
      description: 'Deploy to specific user roles',
      isActive: true
    },
    {
      id: '2',
      name: 'Percentage Rollout',
      description: 'Deploy to % of all users',
      isActive: false
    },
    {
      id: '3',
      name: 'Region-Based Targeting',
      description: 'Deploy to specific regions',
      isActive: false
    },
    {
      id: '4',
      name: 'Custom Rules',
      description: 'Complex targeting rules',
      isActive: false
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'default';
      default: return 'default';
    }
  };

  const getTargetGroupIcon = (group: string) => {
    if (group.includes('Drivers')) return <Person />;
    if (group.includes('Renters')) return <Group />;
    if (group.includes('Hosts')) return <Business />;
    if (group.includes('Region')) return <Timeline />;
    return <Group />;
  };

  const getMetricIcon = (category: string) => {
    switch (category) {
      case 'Conversion': return <TrendingUp />;
      case 'Cancellation': return <Cancel />;
      case 'UX Friction': return <Assessment />;
      default: return <BarChart />;
    }
  };

  const formatMetricValue = (metric: any) => {
    if (metric.name === 'Avg. Booking Time') {
      const minutes = Math.floor(metric.current / 60);
      const seconds = metric.current % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${metric.current}%`;
  };

  const filteredFlags = featureFlags.filter(flag => {
    const matchesSearch = flag.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || flag.status.toLowerCase() === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleRoleToggle = (category: string, role: string) => {
    setRoleTargetingSettings(prev => ({
      ...prev,
      [category]: {
        ...(prev as any)[category],
        roles: {
          ...(prev as any)[category].roles,
          [role]: !(prev as any)[category].roles[role]
        }
      }
    }));
  };

  const handleCategoryToggle = (category: string) => {
    setRoleTargetingSettings(prev => ({
      ...prev,
      [category]: {
        ...(prev as any)[category],
        enabled: !(prev as any)[category].enabled
      }
    }));
  };

  const handleExportData = () => {
    const csvContent = [
      ['Feature Name', 'Status', 'Rollout %', 'Target Group', 'Created', 'Last Modified'].join(','),
      ...featureFlags.map(flag => [
        flag.name,
        flag.status,
        flag.rollout,
        flag.targetGroup,
        flag.created,
        flag.lastModified
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `experimentation-data-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFilterFeatures = () => {
    alert('Feature Filter Options:\n\n• Filter by Status (Active/Inactive)\n• Filter by Target Group\n• Filter by Rollout Percentage\n• Filter by Creation Date\n\nThis would open an advanced filter dialog.');
  };

  const handleCreateToggle = () => {
    if (!newToggleForm.name.trim()) {
      alert('Please enter a toggle name');
      return;
    }

    const newToggle: FeatureFlag = {
      id: Date.now().toString(),
      name: newToggleForm.name,
      status: 'Inactive',
      rollout: newToggleForm.rolloutPercentage,
      targetGroup: newToggleForm.targetGroup as any,
      created: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
      lastModified: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
      description: newToggleForm.description
    };

    setFeatureFlags(prev => [newToggle, ...prev]);
    setNewToggleOpen(false);
    setNewToggleForm({
      name: '',
      description: '',
      targetGroup: 'All Users',
      rolloutPercentage: 0
    });
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Science sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Experimentation Tools
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
            onClick={handleExportData}
            data-testid="export-experiment-data-btn"
          >
            Export Data
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewExperimentOpen(true)}
            sx={{ textTransform: 'none' }}
            data-testid="new-experiment-btn"
          >
            New Experiment
          </Button>
        </Box>
      </Box>

      {/* Feature Flag Management */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Feature Flag Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search features..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
                sx={{ minWidth: 200 }}
              />
              <Button variant="outlined" size="small" startIcon={<FilterList />} onClick={handleFilterFeatures} data-testid="filter-features-btn">
                Filter
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Feature Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rollout</TableCell>
                  <TableCell>Target Group</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFlags.map((flag) => (
                  <TableRow key={flag.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Flag color="primary" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {flag.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={flag.status}
                        size="small"
                        color={getStatusColor(flag.status) as any}
                        icon={flag.status === 'Active' ? <CheckCircle /> : <Schedule />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={flag.rollout}
                          sx={{ width: 60, height: 6, borderRadius: 3 }}
                          color={flag.rollout > 50 ? 'success' : 'primary'}
                        />
                        <Typography variant="body2">{flag.rollout}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getTargetGroupIcon(flag.targetGroup)}
                        <Typography variant="body2">{flag.targetGroup}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {flag.created}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {flag.lastModified}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Analytics">
                          <IconButton size="small">
                            <Assessment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small">
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Experiment Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {experimentMetrics.map((category) => (
          <Grid item xs={12} md={4} key={category.category}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getMetricIcon(category.category)}
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {category.category} Metrics
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select defaultValue={category.timeRange} size="small">
                      <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
                      <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
                      <MenuItem value="Custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Paper sx={{ p: 2, backgroundColor: 'grey.50', mb: 2, textAlign: 'center' }}>
                  {category.category === 'Conversion' && <TrendingUp sx={{ fontSize: 48, color: 'success.main' }} />}
                  {category.category === 'Cancellation' && <Cancel sx={{ fontSize: 48, color: 'warning.main' }} />}
                  {category.category === 'UX Friction' && <PieChart sx={{ fontSize: 48, color: 'info.main' }} />}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {category.category === 'Conversion' && 'Conversion Rate Graph'}
                    {category.category === 'Cancellation' && 'Cancellation Rate Graph'}
                    {category.category === 'UX Friction' && 'UX Friction Distribution'}
                  </Typography>
                </Paper>

                {category.metrics.map((metric, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {metric.name}
                      </Typography>
                      {metric.change !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {metric.isPositive ? (
                            <TrendingUp sx={{ fontSize: 14, color: 'success.main' }} />
                          ) : (
                            <TrendingDown sx={{ fontSize: 14, color: 'error.main' }} />
                          )}
                          <Typography
                            variant="caption"
                            color={metric.isPositive ? 'success.main' : 'error.main'}
                          >
                            {metric.change > 0 ? '+' : ''}{metric.change}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatMetricValue(metric)}
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Experiment Impact
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {category.category === 'Conversion' && 'Instant Booking Flow: +8.4% Conversion'}
                  {category.category === 'Cancellation' && 'Enhanced Verification: -3.2% Cancellations'}
                  {category.category === 'UX Friction' && 'Trust Code UI Redesign: -35% Support Calls'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Toggle Management */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Toggle Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setNewToggleOpen(true)}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              New Feature Toggle
            </Button>
          </Box>

          {/* Rollout Strategies */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Rollout Strategies
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {toggleStrategies.map((strategy) => (
              <Grid item xs={12} sm={6} md={3} key={strategy.id}>
                <Paper
                  sx={{
                    p: 2,
                    border: strategy.isActive ? 2 : 1,
                    borderColor: strategy.isActive ? 'primary.main' : 'divider',
                    backgroundColor: strategy.isActive ? 'primary.50' : 'transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Cached color={strategy.isActive ? 'primary' : 'disabled'} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {strategy.name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {strategy.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Role-Based Targeting Settings */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Role-Based Targeting Settings
          </Typography>

          <Grid container spacing={3}>
            {Object.entries(roleTargetingSettings).map(([category, settings]) => (
              <Grid item xs={12} md={4} key={category}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {category}
                    </Typography>
                    <Switch
                      checked={settings.enabled}
                      onChange={() => handleCategoryToggle(category)}
                      size="small"
                    />
                  </Box>
                  
                  {Object.entries(settings.roles).map(([role, enabled]) => (
                    <FormControlLabel
                      key={role}
                      control={
                        <Checkbox
                          checked={enabled}
                          onChange={() => handleRoleToggle(category, role)}
                          size="small"
                          disabled={!settings.enabled}
                        />
                      }
                      label={role}
                      sx={{ display: 'block', mb: 0.5 }}
                    />
                  ))}
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant="outlined">Cancel</Button>
            <Button variant="contained">Save Changes</Button>
          </Box>
        </CardContent>
      </Card>

      {/* New Experiment Dialog */}
      <Dialog open={newExperimentOpen} onClose={() => setNewExperimentOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Experiment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set up a new A/B test or feature flag experiment
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewExperimentOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Experiment</Button>
        </DialogActions>
      </Dialog>

      {/* New Toggle Dialog */}
      <Dialog open={newToggleOpen} onClose={() => setNewToggleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Feature Toggle</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure a new feature toggle with targeting rules
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="Toggle Name"
              value={newToggleForm.name}
              onChange={(e) => setNewToggleForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Enhanced Payment Flow"
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={newToggleForm.description}
              onChange={(e) => setNewToggleForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of the feature"
              multiline
              rows={2}
            />
            
            <FormControl fullWidth>
              <InputLabel>Target Group</InputLabel>
              <Select
                value={newToggleForm.targetGroup}
                onChange={(e) => setNewToggleForm(prev => ({ ...prev, targetGroup: e.target.value }))}
                label="Target Group"
              >
                <MenuItem value="All Users">All Users</MenuItem>
                <MenuItem value="Drivers">Drivers</MenuItem>
                <MenuItem value="Renters">Renters</MenuItem>
                <MenuItem value="Hosts">Hosts</MenuItem>
                <MenuItem value="Region: Lagos">Region: Lagos</MenuItem>
              </Select>
            </FormControl>
            
            <Box>
              <Typography variant="body2" gutterBottom>
                Initial Rollout Percentage: {newToggleForm.rolloutPercentage}%
              </Typography>
              <Slider
                value={newToggleForm.rolloutPercentage}
                onChange={(_, value) => setNewToggleForm(prev => ({ ...prev, rolloutPercentage: value as number }))}
                min={0}
                max={100}
                step={5}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 50, label: '50%' },
                  { value: 100, label: '100%' }
                ]}
                valueLabelDisplay="auto"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewToggleOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateToggle}>Create Toggle</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};